import { useTranslation } from 'react-i18next';
import {
  Button,
  Dropdown,
  Field,
  Option,
  Spinner,
  Text,
  makeStyles,
} from '@fluentui/react-components';
import { FIELD_LABEL_LAYOUT } from '../OtherInfo/panelLayout';

export interface WorkTypeOption {
  id: number;
  name: string;
}

interface WorkTypeFieldProps {
  mode: 'create' | 'read';
  options: WorkTypeOption[];
  value: number | null;
  onChange?: (value: number | null) => void;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  disabled?: boolean;
}

const NONE = '';

export const workTypeReadLabel = (
  id: number | null,
  options: readonly WorkTypeOption[],
  t: (
    key: string,
    defaultValue: string,
    options?: Record<string, unknown>
  ) => string
): string => {
  if (id == null) return t('noWorkType', 'No work type');
  const known = options.find((option) => option.id === id);
  return (
    known?.name ??
    t('unavailableWorkType', 'Unavailable work type ({{id}})', { id })
  );
};

const useStyles = makeStyles({
  host: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    gridColumn: '1 / -1',
  },
  field: {
    width: '100%',
    minWidth: 0,
    maxWidth: '100%',
    boxSizing: 'border-box',
    alignItems: 'center',
    '& .fui-Field__control': {
      minWidth: 0,
      maxWidth: '100%',
    },
    '& .fui-Dropdown': {
      width: '100%',
      minWidth: '0 !important',
      maxWidth: '100%',
    },
    '& .fui-Field__label': {
      overflowWrap: 'break-word',
      maxWidth: '100%',
      minWidth: 0,
      marginInline: 0,
    },
  },
  value: {
    overflowWrap: 'break-word',
  },
  retry: {
    justifySelf: 'start',
  },
});

export const WorkTypeField = ({
  mode,
  options,
  value,
  onChange,
  loading = false,
  error,
  onRetry,
  disabled = false,
}: WorkTypeFieldProps) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const noneLabel = t('noWorkType', 'No work type');
  const known = options.find((option) => option.id === value);
  const readLabel = workTypeReadLabel(value, options, t);
  const empty = mode === 'create' && !loading && !error && options.length === 0;

  return (
    <div className={styles.host}>
      <Field
        className={styles.field}
        label={t('workType', 'Work type')}
        orientation="horizontal"
        style={{
          ...FIELD_LABEL_LAYOUT,
          alignItems: empty || error ? 'start' : 'center',
        }}
        hint={
          empty ? t('noWorkTypes', 'No work types for this tenant.') : undefined
        }
        validationMessage={error ?? undefined}
        validationState={error ? 'error' : 'none'}
      >
        {loading ? (
          <Spinner size="tiny" label={t('loading', 'Loading...')} />
        ) : error ? (
          <Button
            className={styles.retry}
            appearance="secondary"
            size="small"
            onClick={onRetry}
            disabled={!onRetry}
          >
            {t('retry', 'Retry')}
          </Button>
        ) : mode === 'read' ? (
          <Text className={styles.value}>{readLabel}</Text>
        ) : (
          <Dropdown
            value={known?.name ?? noneLabel}
            selectedOptions={[value == null ? NONE : String(value)]}
            disabled={disabled}
            onOptionSelect={(_, data) => {
              const next = data.optionValue;
              onChange?.(next == null || next === NONE ? null : Number(next));
            }}
          >
            <Option value={NONE}>{noneLabel}</Option>
            {options.map((option) => (
              <Option key={option.id} value={String(option.id)}>
                {option.name}
              </Option>
            ))}
          </Dropdown>
        )}
      </Field>
    </div>
  );
};
